/* -----------------Globals--------------- */
import { DATE_FORMAT } from "@memorang/constants/date";
import { DateTime } from "luxon";
import React, {
	createContext,
	useCallback,
	useEffect,
	useMemo,
	useState,
	type ReactNode,
} from "react";

/* -----------------Child components--------------- */
import { trackCustomEvent, updateUserProperties } from "analytics";

/* -----------------Constants--------------- */
import { events } from "@constants/tracking";

import { cacheKeys } from "@constants/cache-keys";
import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { getExamDaysLeft } from "@features/dashboard/helpers/stats";
import { getLogicalUserId } from "@helpers/auth-provider";
import { getBaseCacheKey } from "@helpers/common";
import { useAppStore } from "@hooks/useAppStore";
import useAuthUser from "@hooks/useAuthUser";
import useCache from "@hooks/useCache";
import type { ExamDetails } from "@memorang/types";
import { useQueryClient } from "@tanstack/react-query";
import {
	fetchExamDetails,
	fetchExamsList,
	updateExamDetails,
} from "api/commerce/exam-service";
import type { AppContextCache } from "types/apps";
import {
	constructClientExamContext,
	retrieveExamContextFromKV,
	storeExamContextInKV,
} from "../helpers";
/* -----------------Local modules--------------- */
import { defaultExamContext } from "./constants";
import type { CurrentExamContext } from "./types";

type ContextValue = {
	currentExamContext: CurrentExamContext;
	updateCurrentExamContext: (examContext: CurrentExamContext) => void;
	updateExamDate: (examDate: Date) => void;
	handleSwitchExamContext: (examDetails: ExamDetails) => Promise<void>;
};

const initExamContext = {
	studyPackProductId: "",
	examName: "",
	examId: "",
	studyPackProductNumericId: 0,
	projectId: 0,
	organizationId: 0,
	studyPackCollectionId: 0,
	practiceTestProductId: "",
};

const Context = createContext<ContextValue>({
	currentExamContext: initExamContext,
	updateCurrentExamContext: () => {
		//
	},
	updateExamDate: () => {
		//
	},
	handleSwitchExamContext: async () => {
		//
	},
});

const ExamContextProvider = ({ children }: { children: ReactNode }) => {
	const [currentExamContext, setCurrentExamContext] =
		useState<CurrentExamContext>(initExamContext);

	const { userLogicalId } = useAuthUser();

	const { viewerId: userId, loading } = useAuthContext();
	const appId = useAppStore((state) => state.app?.id) || "";
	const queryClient = useQueryClient();

	const baseCacheKey = useMemo(
		() =>
			currentExamContext.organizationId &&
			currentExamContext.projectId &&
			appId &&
			userLogicalId
				? getBaseCacheKey({
						orgId: currentExamContext.organizationId,
						projectId: currentExamContext.projectId,
						appId,
					})
				: "",
		[
			currentExamContext.organizationId,
			currentExamContext.projectId,
			appId,
			userLogicalId,
		],
	);

	const appContextCacheKey = baseCacheKey
		? `${baseCacheKey}:user-${userLogicalId}:app-context`
		: "";

	const { setCache: setSelectedStateIdCache } = useCache<AppContextCache>({
		key: appContextCacheKey || "",
		defaultData: { selectedStates: [{ code: "", relatedExamsIds: [] }] },
		enabled: Boolean(appContextCacheKey),
		shouldOptimisticUpdate: true,
	});
	const appContextData = queryClient.getQueryData<AppContextCache>([
		cacheKeys.cache,
		appContextCacheKey || "",
	]);

	const selectedState = useMemo(
		() => appContextData?.selectedStates?.[0],
		[appContextData],
	);

	const relatedExamsIds = useMemo(
		() => selectedState?.relatedExamsIds ?? [],
		[selectedState],
	);

	const updateCurrentExamContext = useCallback(
		async (data: CurrentExamContext) => {
			setCurrentExamContext(data);

			const daysLeft = getExamDaysLeft(data.examDate);

			const userId = userLogicalId ?? (await getLogicalUserId());
			if (userId) {
				storeExamContextInKV(data);
			}
			trackCustomEvent({
				eventName: events.examContextSet,
				examName: data.examName,
				examId: data.examId,
			});
			updateUserProperties({
				mem_exam_name: data.examName,
				mem_exam_id: String(data.examId),
				mem_exam_date: data.examDate ?? "not-set",
				mem_exam_interval: `${daysLeft} days`,
			});
		},
		[userLogicalId],
	);

	const setDefaultExamContext = useCallback(() => {
		setCurrentExamContext(defaultExamContext);
	}, []);

	const getExamContextWithDate = useCallback(
		async (cachedContext: CurrentExamContext): Promise<CurrentExamContext> => {
			try {
				const examDetails = await fetchExamDetails({
					examId: cachedContext.examId,
				});

				const studyPackCollectionId =
					cachedContext.studyPackCollectionId ||
					examDetails.exam.studyPackCollection[0];

				const practiceTest = examDetails.exam.bundles.find(
					(item) => item.bundle.bundleType === "PRACTICE_TEST",
				)?.bundle;

				const practiceTestProductNumericId =
					cachedContext.practiceTestProductNumericId || practiceTest?.numericId;

				return {
					...cachedContext,
					examDate: examDetails.metadata.examDate,
					studyPackCollectionId,
					practiceTestProductNumericId,
				};
			} catch (error) {
				console.error("Failed to fetch exam details:", error);
				return cachedContext;
			}
		},
		[],
	);

	const createExamContextFromPurchased = useCallback(
		(examDetails: ExamDetails): CurrentExamContext => {
			const practiceTest = examDetails.exam.bundles.find(
				(item) => item.bundle.bundleType === "PRACTICE_TEST",
			)?.bundle;
			const studyPack = examDetails.exam.bundles.find(
				(item) => item.bundle.bundleType === "STUDY_PACK",
			)?.bundle;

			return {
				projectId: defaultExamContext.projectId,
				organizationId: defaultExamContext.organizationId,
				studyPackCollectionId: examDetails.exam.studyPackCollection[0],
				studyPackProductId: studyPack?.id!,
				practiceTestProductId: practiceTest?.id!,
				studyPackProductNumericId: studyPack?.numericId!,
				examName: examDetails.exam.name,
				examId: examDetails.exam.id,
				examDate: examDetails.metadata.examDate,
				examIcon: examDetails.exam.icon,
				practiceTestProductNumericId: practiceTest?.numericId,
			};
		},
		[],
	);

	const handleCachedExamContext = useCallback(
		async (cachedExamContext: CurrentExamContext) => {
			setCurrentExamContext(cachedExamContext);
			const updatedContext = await getExamContextWithDate(cachedExamContext);

			const wasUpdated =
				(!cachedExamContext.studyPackCollectionId &&
					updatedContext.studyPackCollectionId) ||
				(!cachedExamContext.practiceTestProductNumericId &&
					updatedContext.practiceTestProductNumericId);

			if (wasUpdated) {
				await updateCurrentExamContext(updatedContext);
			} else {
				setCurrentExamContext(updatedContext);
			}
		},
		[getExamContextWithDate, updateCurrentExamContext],
	);

	const syncExamContext = useCallback(async () => {
		if (loading || !userId) {
			return;
		}

		try {
			const cachedExamContext = await retrieveExamContextFromKV();

			if (cachedExamContext) {
				await handleCachedExamContext({
					...cachedExamContext,
					projectId: defaultExamContext.projectId,
					organizationId: defaultExamContext.organizationId,
					documentsCollectionId: defaultExamContext.documentsCollectionId,
				});
				return;
			}

			const latestExams = await fetchExamsList();
			const purchasedExam = latestExams.find((item) =>
				item.exam.bundles.some(
					(bundle) => bundle.metadata?.subscription?.status === "ACTIVE",
				),
			);

			if (!purchasedExam) {
				setDefaultExamContext();
				return;
			}

			const newExamContext = createExamContextFromPurchased(purchasedExam);
			await storeExamContextInKV(newExamContext);
			setCurrentExamContext(newExamContext);
		} catch (_error) {
			setDefaultExamContext();
		}
	}, [
		userId,
		setDefaultExamContext,
		createExamContextFromPurchased,
		handleCachedExamContext,
		loading,
	]);
	useEffect(() => {
		syncExamContext();
	}, [syncExamContext]);

	const updateExamDate = useCallback(
		(examDate: Date) => {
			if (examDate) {
				const formattedDate = DateTime.fromJSDate(examDate, {
					zone: "local",
				}).toFormat(DATE_FORMAT);

				if (currentExamContext.examId) {
					trackCustomEvent({
						eventName: events.examDateSet,
						examDate: formattedDate,
					});
					updateExamDetails({
						examId: currentExamContext.examId,
						metadata: {
							examDate: formattedDate,
						},
					});

					updateCurrentExamContext({
						...currentExamContext,
						examDate: formattedDate,
					});
				}
			}
		},
		[currentExamContext, updateCurrentExamContext],
	);

	const handleSwitchExamContext = useCallback(
		async (examDetails: ExamDetails) => {
			const isAssociatedWithTheCurrentState = relatedExamsIds.includes(
				examDetails.exam.id,
			);
			if (!isAssociatedWithTheCurrentState) {
				setSelectedStateIdCache({
					selectedStates: [
						{
							code: examDetails.exam.state?.code ?? "",
							relatedExamsIds: examDetails.exam.relatedExams ?? [],
						},
					],
				});
			}
			const constructedExamContext = constructClientExamContext(examDetails);

			await updateCurrentExamContext({
				...constructedExamContext,
				projectId: defaultExamContext.projectId,
				organizationId: defaultExamContext.organizationId,
			});
		},
		[updateCurrentExamContext, setSelectedStateIdCache, relatedExamsIds],
	);

	const value = useMemo(() => {
		return {
			currentExamContext,
			updateCurrentExamContext,
			updateExamDate,
			handleSwitchExamContext,
		};
	}, [
		currentExamContext,
		handleSwitchExamContext,
		updateCurrentExamContext,
		updateExamDate,
	]);

	return <Context.Provider value={value}>{children}</Context.Provider>;
};

const useExamContext = () => {
	const context = React.useContext(Context);
	if (context === undefined) {
		throw new Error("useExamContext must be used within a ExamContextProvider");
	}
	return context;
};

export { ExamContextProvider, useExamContext };
